import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AdminCatalogDoc, AdminManifest } from "@equipped/core/backend/client";
import { FieldForm } from "../FieldForm";
import { catalogPackEntryFields } from "../catalogPackEntryFields";
import { catalogNodeEntryFields } from "../catalogNodeEntryFields";
import { renderFields } from "../fieldTypes";
import { gatedCatalogUrl, gatedManifestUrl, ownedCatalogIdFromUrl, ownedPackIdFromUrl } from "../entryUrls";

const PACK_FIELDS = renderFields(catalogPackEntryFields);
const CATALOG_FIELDS = renderFields(catalogNodeEntryFields);
const CUSTOM = "__custom__";

interface EntryDraft {
  kind: "pack" | "catalog";
  id: string;
  title: string;
  manifestUrl?: string;
  url?: string;
  [key: string]: unknown;
}

/**
 * The catalog-ENTRY (placement) properties panel — `CatalogPackEntry`/
 * `CatalogNodeEntry`, distinct from the DOCUMENT it references (docs/343 v0 §2's
 * "Placement vs. Document" split). No independent Save/Discard: an edit here
 * patches the PARENT catalog draft in place via `onChange` — it's saved (or
 * discarded) together with that catalog document.
 */
export function EntryPanel({
  entry,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  onOpenPack,
  onOpenCatalog,
  backendUrl,
  manifestIndex,
  catalogIndex,
}: {
  entry: EntryDraft;
  onChange: (next: EntryDraft) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onOpenPack: (id: string) => void;
  onOpenCatalog: (id: string) => void;
  backendUrl: string;
  manifestIndex: AdminManifest[];
  catalogIndex: AdminCatalogDoc[];
}) {
  const isPack = entry.kind === "pack";
  const ownedId = isPack ? ownedPackIdFromUrl(backendUrl, entry.manifestUrl) : ownedCatalogIdFromUrl(backendUrl, entry.url);

  const pickOwned = (id: string) => {
    if (id === CUSTOM) return;
    if (isPack) {
      const row = manifestIndex.find((m) => m.id === id);
      onChange({ ...entry, id, title: row?.title ?? entry.title, manifestUrl: gatedManifestUrl(backendUrl, id) });
    } else {
      const row = catalogIndex.find((c) => c.id === id);
      onChange({ ...entry, id, title: row?.title ?? entry.title, url: gatedCatalogUrl(backendUrl, id) });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0 }}>
          {entry.title || "(untitled entry)"}
        </Typography>
        <Chip label={entry.kind} size="small" />
      </Box>

      <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Target
        </Typography>
        {!backendUrl ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            No backend configured — can't auto-fill a URL from one of your own saved
            {isPack ? " packs" : " catalogs"}.
          </Alert>
        ) : (
          <TextField
            select
            fullWidth
            size="small"
            label={isPack ? "One of my saved packs" : "One of my saved catalogs"}
            value={ownedId ?? CUSTOM}
            onChange={(e) => pickOwned(e.target.value)}
            sx={{ mb: 1 }}
          >
            <MenuItem value={CUSTOM}>
              <em>Custom / external URL</em>
            </MenuItem>
            {(isPack ? manifestIndex : catalogIndex).map((row) => (
              <MenuItem key={row.id} value={row.id}>
                {row.title} ({row.id})
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          fullWidth
          size="small"
          label={isPack ? "manifestUrl" : "url"}
          value={(isPack ? entry.manifestUrl : entry.url) ?? ""}
          onChange={(e) =>
            onChange(isPack ? { ...entry, manifestUrl: e.target.value } : { ...entry, url: e.target.value })
          }
        />
        {ownedId ? (
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => (isPack ? onOpenPack(ownedId) : onOpenCatalog(ownedId))}
          >
            Open {isPack ? "pack manifest" : "catalog"} →
          </Button>
        ) : null}
      </Box>

      <FieldForm
        fields={isPack ? PACK_FIELDS : CATALOG_FIELDS}
        value={entry}
        onChange={(next) => onChange(next as EntryDraft)}
      />

      <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
        {onMoveUp ? (
          <Button size="small" variant="outlined" onClick={onMoveUp}>
            ▲ Move up
          </Button>
        ) : null}
        {onMoveDown ? (
          <Button size="small" variant="outlined" onClick={onMoveDown}>
            ▼ Move down
          </Button>
        ) : null}
        <Button size="small" color="error" onClick={onRemove}>
          Remove entry
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Saved together with the catalog document.
      </Typography>
    </Box>
  );
}
